import React, { FunctionComponent } from 'react';
import { useLocation } from 'react-router-dom';
import { Grid } from 'semantic-ui-react';
import { ApolloError } from 'apollo-boost';
import { pathOr } from 'ramda';
import queryString from 'query-string';

import ReportBase, { ReportParams } from 'scenes/Report/components/ReportBase';
import GET_DEMOGRAPHIC_SEGMENTS from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/Demographics/Demographics.queries';
import Query from 'scenes/Report/components/Query';
import {
  DemographicsAgeChart,
  DemographicsGenderChart,
  DemographicsIncomeChart,
} from 'components/Charts';
import { DemographicSegments } from 'components/Charts/Demographics/Demographics.helper';

type Props = {
  isAnimationActive?: boolean;
  height?: number;
};

type QueryResponse = {
  data?: DemographicSegments;
  loading?: boolean;
  error?: ApolloError;
};

const renderReport = (params: ReportParams, { isAnimationActive }: Props) => (
  response: QueryResponse,
) => {
  const demographicsData = pathOr(
    [],
    ['demographicSegments'],
    response,
  ) as DemographicSegments;

  const { age, income, gender } = demographicsData;

  return (
    <ReportBase
      batchKey="shopperDemographics"
      header="Shopper Demographics"
      params={params}
    >
      <Grid.Row>
        <Grid.Column width={6}>
          <DemographicsAgeChart
            title="Age"
            ageGroups={age}
            isAnimationActive={isAnimationActive}
          />
        </Grid.Column>

        <Grid.Column width={4}>
          <DemographicsGenderChart
            innerRadius={50}
            outerRadius={90}
            title="Gender"
            genderGroup={gender}
          />
        </Grid.Column>

        <Grid.Column width={6}>
          <DemographicsIncomeChart
            title="Household Income"
            isAnimationActive={isAnimationActive}
            incomeGroups={income}
          />
        </Grid.Column>
      </Grid.Row>
    </ReportBase>
  );
};

const Demographics: FunctionComponent<Props> = (props) => {
  const location = useLocation();
  const params = queryString.parse(location.search) as ReportParams;

  return (
    <Query
      query={GET_DEMOGRAPHIC_SEGMENTS}
      reportName="Shopper Demographics"
      batchKey="shopperDemographics"
      queryKey="demographicSegments"
      render={renderReport(params, props)}
      variables={{
        params,
      }}
    />
  );
};

export default Demographics;
